import { Badge } from "@acme/ui/badge";
import { Card } from "@acme/ui/card";
import { Label } from "@acme/ui/label";
import { Switch } from "@acme/ui/switch";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { statBadgeNeutral } from "~/common/components/stat-badge-styles.js";
import { surfacePanel } from "~/common/components/surface-styles.js";
import { FUTURE_NOTIFICATION_CHANNEL_KEYS } from "~/common/constants/notification-channels.js";
import useClanDetailAccess from "~/features/clan-detail/_hooks/useClanDetailAccess.js";
import useClanFeatureAccess from "~/features/clan-permissions/_hooks/useClanFeatureAccess.js";
import useClanPushNotificationSetting from "~/features/clan-notifications/_hooks/useClanPushNotificationSetting.js";

export const Route = createFileRoute("/_authed/my-clans/$tag/notifications")({
  component: NotificationsTab,
});

function NotificationsTab() {
  const { t } = useTranslation();
  const { tag } = Route.useParams();
  const { isReady, effectivePermissions } = useClanDetailAccess({ tag });
  const { canRead } = useClanFeatureAccess({ domain: "notifications", permissions: effectivePermissions });
  const { pushEnabled, isLoading, isPending, setPushEnabled } = useClanPushNotificationSetting({
    clanTag: tag,
    enabled: isReady && canRead,
  });

  const futureChannelOptions = useMemo(
    () =>
      FUTURE_NOTIFICATION_CHANNEL_KEYS.map(({ value, labelKey }) => ({
        value,
        label: t(`app.placeholders.${labelKey}`),
      })),
    [t]
  );

  if (!isReady || !canRead) return null;

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-1">{t("app.placeholders.clanNotificationChannelsTitle")}</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {t("app.placeholders.clanNotificationChannelsDescription")}
          </p>
          <div className="space-y-4">
            <Label>{t("app.placeholders.channels")}</Label>
            <ul className="space-y-3">
              <li className={`${surfacePanel} px-4 py-3 space-y-1`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <span className="text-sm font-medium">{t("app.placeholders.channelPush")}</span>
                    <p className="text-xs text-muted-foreground">
                      {t("app.placeholders.clanPushNotificationDescription")}
                    </p>
                  </div>
                  <Switch
                    aria-label={t("app.placeholders.enableChannel", {
                      channel: t("app.placeholders.channelPush"),
                    })}
                    checked={pushEnabled}
                    disabled={isLoading || isPending}
                    onCheckedChange={setPushEnabled}
                  />
                </div>
              </li>
              {futureChannelOptions.map(({ value, label }) => (
                <li
                  className={`flex items-center justify-between ${surfacePanel} px-4 py-3`}
                  key={value}>
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-sm font-medium">{label}</span>
                    <Badge className={statBadgeNeutral} variant="outline">
                      {t("app.placeholders.futureFeatureBadge")}
                    </Badge>
                  </div>
                  <Switch
                    aria-label={t("app.placeholders.enableChannel", { channel: label })}
                    checked={false}
                    disabled
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
}
